// hooks/customer/useCustomer.ts
import { useCallback, useEffect, useRef, useState } from 'react';
import { getAssociatedCustomers, getCustomerList } from '@/api';
import {
    readSelectedCustomerId,
    readSelectedDateRange,
    writeSelectedCustomerId,
    writeSelectedDateRange,
} from '@/utils';
import type { Customer } from '@/types/customers';
import type { DateRange } from '@/types';

const getStoredCustomerId = (): number | null => readSelectedCustomerId();

const getStoredDateRange = (): DateRange => {
    const stored = readSelectedDateRange();
    return stored
        ? JSON.parse(stored)
        : {
              startDate: new Date(new Date().setDate(new Date().getDate() - 30)),
              endDate: new Date(),
          };
};

export const useCustomer = () => {
    const [selectedCustomerId, setSelectedCustomerIdState] = useState<number | null>(getStoredCustomerId);
    const [selectedDateRange, setSelectedDateRange] = useState<DateRange | null>(getStoredDateRange);
    const [customerData, setCustomerData] = useState<Customer[]>([]);
    const [loading, setLoading] = useState(true);

    // Unique origin id for this hook instance — used to ignore our own broadcasts
    const originRef = useRef(Math.random().toString(36).slice(2));

    const setSelectedCustomerId = useCallback((newId: number | null) => {
        setSelectedCustomerIdState(newId);
        writeSelectedCustomerId(newId);

        try {
            window.dispatchEvent(
                new CustomEvent('selectedCustomerIdUpdated', {
                    detail: { id: newId, origin: originRef.current },
                })
            );
        } catch (e) {
            console.warn('Failed to dispatch selectedCustomerIdUpdated event', e);
        }
    }, []);

    // Sync with other hook instances in the same tab.
    //
    // Deliberately NOT listening for the cross-tab `storage` event any more. The
    // chameleon/demo identity is per-tab (sessionStorage); the selection used to be
    // cross-tab (localStorage), so a demo tab and a normal tab adopted each other's
    // customer id and then fetched it under their own identity — a 403 ping-pong
    // that produced the intermittent demo-mode failures. Selection is now per-tab
    // too, and the two tabs leave each other alone.
    useEffect(() => {
        const handler = (e: Event) => {
            const detail = (e as CustomEvent<{ id: number | null; origin: string }>).detail;
            if (!detail || detail.origin === originRef.current) return;
            setSelectedCustomerIdState(detail.id ?? null);
        };

        window.addEventListener('selectedCustomerIdUpdated', handler);
        return () => window.removeEventListener('selectedCustomerIdUpdated', handler);
    }, []);

    const refreshCustomers = useCallback(async (): Promise<Customer[]> => {
        setLoading(true);
        try {
            let data = await getAssociatedCustomers();

            if ((!data || data.length === 0) && typeof getCustomerList === 'function') {
                try {
                    data = (await getCustomerList()) as Customer[];
                } catch (e) {
                    console.warn('useCustomer: getCustomerList fallback failed:', e);
                }
            }

            setCustomerData(data);

            // Enforce the invariant: the selected customer must belong to this entity's list.
            // After a chameleon switch (or exit) the previous selection may be invisible to the
            // new entity — coerce to the first available customer (or null if it has none) in the
            // same commit as the new list, so no surface fires a request with a stale id (the
            // occasional post-chameleon 403). Applies to every useCustomer consumer, campaigns
            // surface included — which previously had no fall-back-to-first of its own.
            if (data.length === 0) {
                if (selectedCustomerId !== null) setSelectedCustomerId(null);
            } else if (!selectedCustomerId || !data.some((c) => c.id === selectedCustomerId)) {
                setSelectedCustomerId(data[0].id);
            }

            return data;
        } catch (error) {
            console.error('Error refreshing customers:', error);
            return [];
        } finally {
            setLoading(false);
        }
    }, [selectedCustomerId, setSelectedCustomerId]);

    // Fetch on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        refreshCustomers();
    }, []);

    const handleCustomerChange = (newCustomerId: number | null) => {
        if (newCustomerId !== selectedCustomerId) {
            setSelectedCustomerId(newCustomerId);
            setSelectedDateRange({
                startDate: new Date(new Date().setDate(new Date().getDate() - 30)),
                endDate: new Date(),
            });
        }
    };

    // Keep storage in sync when selectedCustomerId changes
    useEffect(() => {
        if (selectedCustomerId !== null && selectedCustomerId !== undefined) {
            if (readSelectedCustomerId() !== selectedCustomerId) {
                writeSelectedCustomerId(selectedCustomerId);
            }
        }
    }, [selectedCustomerId]);

    // Persist date range
    useEffect(() => {
        if (selectedDateRange?.startDate && selectedDateRange?.endDate) {
            writeSelectedDateRange(JSON.stringify(selectedDateRange));
        }
    }, [selectedDateRange]);

    return {
        selectedCustomerId,
        setSelectedCustomerId,
        selectedDateRange,
        setSelectedDateRange,
        customerData,
        handleCustomerChange,
        loading,
        refreshCustomers,
    };
};
